import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import * as usersService from '../services/api/users';
import { transformUser } from '../services/transformers/userTransformer';
import { getUserId } from '../services/api/config';
import './EditProfile.css';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [image, setImage] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      const userId = getUserId();
      if (!userId) {
        setInitialLoading(false);
        return;
      }

      try {
        const result = await usersService.getUserById(userId);
        if (result.success && result.data) {
          const transformedUser = transformUser(result.data);
          setFormData({
            name: transformedUser.name || '',
            email: transformedUser.email || '',
            phone: transformedUser.phone || ''
          });
          setImage(transformedUser.image || null);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
      setImage(user.image || null);
      setInitialLoading(false);
    } else {
      fetchUserData();
    }
  }, [user]);

  if (!user && !initialLoading) {
    navigate('/login');
    return null;
  }

  if (initialLoading) {
    return (
      <div className="edit-profile-screen">
        <div className="edit-profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setShowImageModal(true);
    }
    e.target.value = '';
  };

  const handleConfirmImageChange = async () => {
    if (!newImageFile) return;
    
    setImageUploading(true);
    setShowImageModal(false);
    
    try {
      const uploadResult = await usersService.uploadProfileImage(newImageFile);
      
      if (uploadResult.success && uploadResult.imageUrl) {
        const userId = getUserId();
        if (userId) {
          const updateResult = await usersService.updateUser(userId, {
            photo: uploadResult.imageUrl
          });
          
          if (updateResult.success) {
            setImage(uploadResult.imageUrl);
            setSuccess('Profile photo updated successfully');
            
            if (updateUser) {
              await updateUser({ image: uploadResult.imageUrl });
            }
          } else {
            setError('Failed to update profile photo');
          }
        }
      } else {
        setError(uploadResult.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Error updating profile image:', err);
      setError('Failed to update profile photo. Please try again.');
    } finally {
      setImageUploading(false);
      setNewImageFile(null);
      setNewImagePreview(null);
    }
  };

  const handleCancelImageChange = () => {
    setShowImageModal(false);
    setNewImageFile(null);
    setNewImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!formData.name || !formData.email) {
      setError('Name and email are required');
      setLoading(false);
      return;
    }

    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const updates = {
        name: formData.name,
        email: formData.email
      };

      if (formData.phone) {
        updates.phone_number = formData.phone;
      }

      const userId = getUserId();
      if (!userId) {
        setError('User ID not found');
        setLoading(false);
        return;
      }

      const result = await usersService.updateUser(userId, updates);

      if (result.success) {
        if (updateUser) {
          await updateUser(updates);
        }
        setSuccess('Profile updated successfully');
        setTimeout(() => {
          navigate('/account');
        }, 1500);
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-profile-screen">
      {/* Header */}
      <header className="edit-header">
        <button className="back-btn" onClick={() => navigate('/account')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1>Edit Profile</h1>
        <div className="header-spacer"></div>
      </header>

      <div className="edit-content">
        {/* Profile Photo Card */}
        <div className="photo-card">
          <label htmlFor="profile-image-upload" className="photo-wrapper">
            <div className="photo-container">
              {imageUploading ? (
                <div className="photo-loading">
                  <div className="photo-spinner"></div>
                </div>
              ) : image ? (
                <img src={image} alt="Profile" className="photo-img" />
              ) : (
                <div className="photo-placeholder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
              <div className="photo-edit-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </div>
            </div>
            <span className="photo-hint">Tap to change photo</span>
          </label>
          <input
            type="file"
            id="profile-image-upload"
            accept="image/*"
            onChange={handleImageSelect}
            hidden
            disabled={imageUploading}
          />
        </div>

        {/* Form Card */}
        <div className="form-card">
          <form onSubmit={handleSubmit}>
            {/* Full Name Field */}
            <div className="input-group">
              <label>Full Name</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="input-group">
              <label>Email Address</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Phone Field */}
            <div className="input-group">
              <label>Phone Number</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            {/* Messages */}
            {error && (
              <div className="alert alert-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22,4 12,14.01 9,11.01" />
                </svg>
                <span>{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                    <polyline points="17,21 17,13 7,13 7,21" />
                    <polyline points="7,3 7,8 15,8" />
                  </svg>
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Cancel Button */}
        <button className="cancel-btn" onClick={() => navigate('/account')}>
          Cancel
        </button>
      </div>

      {/* Image Confirmation Modal */}
      {showImageModal && (
        <div className="modal-overlay" onClick={handleCancelImageChange}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Profile Photo</h3>
              <button className="modal-close" onClick={handleCancelImageChange}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-preview">
                {newImagePreview && (
                  <img src={newImagePreview} alt="Preview" />
                )}
              </div>
              <p className="modal-text">This will be your new profile photo</p>
            </div>

            <div className="modal-footer">
              <button className="modal-btn secondary" onClick={handleCancelImageChange}>
                Cancel
              </button>
              <button className="modal-btn primary" onClick={handleConfirmImageChange}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20,6 9,17 4,12" />
                </svg>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
